import 'bootstrap/dist/css/bootstrap.css';
import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faSearch } from '@fortawesome/free-solid-svg-icons';
import ReactInputMask from 'react-input-mask';
import DateInput from '../../../shared/dateInput';
import Spinner from '../../../shared/spinner';
import CustomAlert from '../../../shared/customAlert';
import SerasaService, { SerasaConsultaRequest } from '../../../services/serasaService';
import CadastroSerasaResultDisplay from './serasaCadastroResultDisplay';
import ConsultaSerasaResultDisplay from './serasaConsultaResultDisplay';

type EndpointSerasa = 'enviar' | 'consultar';

interface FileItem {
  file: File | null;
  id: number;
}

const initialConsultarState = {
  cpfcnpj: '',
  dataCadastroINI: '',
  dataCadastroFIM: '',
  protocoloBrFlow: ''
};

function ConsultaSerasaComponent() {

  const [selectedEndpoint, setSelectedEndpoint] = useState<EndpointSerasa>('enviar');
  const [cpfEnviar, setCpfEnviar] = useState('');
  const [arquivosEnviar, setArquivosEnviar] = useState<FileItem[]>([{ file: null, id: Date.now() }]);
  const [formConsulta, setFormConsulta] = useState(initialConsultarState);
  const [resultado, setResultado] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ message: string, type: 'success' | 'warning' | 'error' } | null>(null);

  const serasaService = new SerasaService();

  const handleEndpointChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const endpoint = e.target.value as EndpointSerasa;
    setSelectedEndpoint(endpoint);
    setResultado(null);
    setAlert(null);

    if (endpoint === 'enviar') {
        setCpfEnviar('');
        setArquivosEnviar([{ file: null, id: Date.now() }]);
    } else {
        setFormConsulta(initialConsultarState);
    }
    
  }
  
  const handleFileChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null;
    const list = [...arquivosEnviar];
    list[index].file = file;
    setArquivosEnviar(list);
  }

  const addFileItem = () => {
    setArquivosEnviar([...arquivosEnviar, { file: null, id: Date.now() }]);
  }

  const removeFileItem = (index: number) => {
    const list = [...arquivosEnviar];
    list.splice(index, 1);
    setArquivosEnviar(list);
  }

  const handleConsultaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormConsulta({ ...formConsulta, [e.target.name]: e.target.value });
  }

  const handleSearch = async () => {
    setLoading(true);
    setResultado(null);
    setAlert(null);

    try {
      let response: any;
      switch (selectedEndpoint) {
        case 'enviar': {
          if (!cpfEnviar) {
            throw new Error("O campo 'Número do CPF' é obrigatório.");
          }
          
          const validFiles = arquivosEnviar
            .map(item => item.file)
            .filter((f): f is File => f !== null);

          if (validFiles.length === 0) {
            throw new Error("Pelo menos um arquivo deve ser selecionado.");
          }

          const cpfClean = cpfEnviar.replace(/\D/g, '');
          
          response = await serasaService.cadastrarRegistro(cpfClean, validFiles);
          break;
        }

        case 'consultar': {
          if (!formConsulta.cpfcnpj) { throw new Error("O campo 'Cpf/Cnpj' é obrigatório."); }
          
          const consultaPayload: SerasaConsultaRequest = {
            cpfcnpj: formConsulta.cpfcnpj.replace(/\D/g, ''),
            dataCadastroINI: formConsulta.dataCadastroINI,
            dataCadastroFIM: formConsulta.dataCadastroFIM,
            protocoloBrFlow: formConsulta.protocoloBrFlow
          };
          response = await serasaService.consultarRegistro(consultaPayload);
          break;
        }
      }
      setResultado(response);
      setAlert({ message: 'Requisição realizada com sucesso!', type: 'success' });
    } catch (error) {
      const errorMessage = (error as any).response?.data || (error as Error).message || 'Ocorreu um erro desconhecido.';
      setAlert({ message: `Erro na Requisição: ${errorMessage}`, type: 'error' });
      console.error('Erro na Requisição:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderFormFields = () => {
    switch (selectedEndpoint) {
      case 'enviar':
        return (
          <div className="col-12">
            <div className="mb-3 col-md-4">
                <label className="form-label fw-bold">Número do CPF <span className="text-danger">*</span></label>
                <ReactInputMask 
                    className="form-control" 
                    mask="999.999.999-99" 
                    placeholder="Digite o CPF" 
                    value={cpfEnviar} 
                    onChange={(e) => setCpfEnviar(e.target.value)} 
                />
            </div>

            <label className="form-label fw-bold mt-2">Arquivos (Pelo menos 1 obrigatório)</label>
            {arquivosEnviar.map((item, index) => (
              <div key={item.id} className="input-group mb-2">
                <input 
                    type="file" 
                    className="form-control" 
                    accept="image/png, image/jpeg, application/pdf" 
                    onChange={(e) => handleFileChange(index, e)} 
                />
                <button className="btn btn-outline-danger" type="button" onClick={() => removeFileItem(index)}>
                    <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            ))}
            
            <div className="col-12 mb-3">
                <button className="btn btn-sm btn-outline-primary" type="button" onClick={addFileItem}>
                    <FontAwesomeIcon icon={faPlus} /> Adicionar outro arquivo
                </button>
            </div>
          </div>
        );

      case 'consultar':
        const maskCpfCnpj = formConsulta.cpfcnpj?.replace(/\D/g, '').length > 11 ? '99.999.999/9999-99' : '999.999.999-99';
        return (
          <>
            <div className="col-md-4">
              <label className="form-label">CPF/CNPJ <span className="text-danger">*</span></label>
              <ReactInputMask name="cpfcnpj" className="form-control" mask={maskCpfCnpj} placeholder="Digite o CPF ou CNPJ" value={formConsulta.cpfcnpj} onChange={handleConsultaChange} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Protocolo</label>
              <input type="text" name="protocoloBrFlow" className="form-control" value={formConsulta.protocoloBrFlow} onChange={handleConsultaChange} />
            </div>
            <div className="col-md-2">
              <label className="form-label">Data Início</label>
              <DateInput name="dataCadastroINI" className="form-control" placeholder="dd/mm/aaaa" value={formConsulta.dataCadastroINI} onChange={handleConsultaChange} />
            </div>
            <div className="col-md-2">
              <label className="form-label">Data Fim</label>
              <DateInput name="dataCadastroFIM" className="form-control" placeholder="dd/mm/aaaa" value={formConsulta.dataCadastroFIM} onChange={handleConsultaChange} />
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {alert && <CustomAlert message={alert.message} type={alert.type} onClose={() => setAlert(null)} />}
      <div className="container-fluid mt-3">
        <div className="card">
          <div className="card-header bg-light"><h5 className="mb-0">Integração Serasa</h5></div>
          <div className="card-body">
            {/* <div className="row g-3 mb-3">
                <div className="col-md-4">
                    <label htmlFor="endpoint-selector" className="form-label">Selecione a Operação</label>
                    <select id="endpoint-selector" className="form-select" value={selectedEndpoint} onChange={handleEndpointChange}>
                        <option value="enviar">Enviar Documento (Análise)</option>
                        <option value="consultar">Consultar Registros</option>
                    </select>
                </div>
            </div> */} {/* comentado para futuramente voltar a ser discutido o endpoint de consultar registro */}

            <div className="row g-3 align-items-end mb-3">
                {renderFormFields()}
            </div>

            <div className="row">
              <div className="col-12">
                <button className="btn btn-primary" onClick={handleSearch} disabled={loading}>
                  <FontAwesomeIcon icon={faSearch} className="me-2" />
                  {loading ? 'Processando...' : 'Executar'}
                </button>
              </div>
            </div>
            
            {loading && <Spinner loading={loading} />}
            
            {!loading && resultado && (
              <>
                  {selectedEndpoint === 'enviar' && <CadastroSerasaResultDisplay data={resultado} />}
                  {selectedEndpoint === 'consultar' && <ConsultaSerasaResultDisplay data={resultado} />}
              </>
            )}
            
          </div>
        </div>
      </div>
    </>
  );
}

export default ConsultaSerasaComponent;